import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Shield } from "lucide-react";

// Searchable club picker fed by the page's existing `teams` array — usable even for 30+ team leagues.
export const TeamSelect = ({ teams = [], value, onChange, className = "", testId = "team-select", placeholder = "Select club" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = teams.find((t) => t.id === Number(value));
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? teams.filter((x) => (x.name || "").toLowerCase().includes(t)) : teams;
  }, [q, teams]);

  const pick = (id) => { onChange(Number(id)); setOpen(false); setQ(""); };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <button type="button" data-testid={testId} aria-haspopup="listbox" onClick={() => setOpen((o) => !o)} disabled={!teams.length}
        className="flex w-full min-w-[200px] items-center justify-between gap-2 rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none transition hover:border-cyan-400/40 disabled:opacity-40">
        <span className="flex items-center gap-2 truncate">
          {current?.logo ? <img src={current.logo} alt="" className="h-4 w-4 object-contain" /> : <Shield size={14} className="text-white/40" />}
          <span className="truncate">{teams.length ? (current ? current.name : placeholder) : "No clubs (season off?)"}</span>
        </span>
        <ChevronDown size={15} className={`shrink-0 text-white/40 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && teams.length > 0 && (
        <div className="absolute z-50 mt-2 w-[280px] overflow-hidden rounded-xl border border-white/12 bg-[#0b0b0d] shadow-[0_20px_60px_rgba(0,0,0,0.7)]" data-testid={`${testId}-panel`}>
          <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2">
            <Search size={14} className="text-white/40" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clubs…"
              data-testid={`${testId}-search`} className="w-full bg-transparent text-sm text-white placeholder:text-white/30 outline-none" />
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-white/40">No club matches “{q}”.</p>
            ) : filtered.map((t) => (
              <button key={t.id} type="button" onClick={() => pick(t.id)} data-testid={`team-option-${t.id}`}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition hover:bg-white/5 ${t.id === Number(value) ? "bg-cyan-400/10 text-cyan-200" : "text-white/80"}`}>
                {t.logo ? <img src={t.logo} alt="" className="h-4 w-4 object-contain" /> : <Shield size={12} className="text-white/30" />}
                <span className="truncate">{t.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
